import { Drawer } from "@mui/material";
import { LoadState } from "@redotech/react-util/load";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { canPurchaseLabelStatus } from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { OMSUser } from "@redotech/redo-fulfillment-group-common/oms-user";
import { Parcel } from "@redotech/redo-fulfillment-parcel-common/parcel";
import { useAiChatPanelState } from "@redotech/redo-merchant-app-common/ask-redo/use-ai-chat-panel-state";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { Flex } from "@redotech/redo-web/flex";
import { CSSProperties, memo, useMemo } from "react";
import { useFulfillmentGroupPermissions } from "../utils/use-assigned-fulfillment-service";
import { FulfillmentForm } from "./fulfillment-group-form";
import { FulfillmentMixedStatuses } from "./fulfillment-group-slideout/mixed-statuses";
import {
  FulfillmentPartiallySelectedDeselect,
  FulfillmentPartiallySelectedSplit,
} from "./fulfillment-group-slideout/partially-selected";
import * as ordersCss from "./outbound-labels.module.css";
export const OrderFulfillmentSlideout = memo(function OrderFulfillmentSlideout({
  fulfillmentGroups,
  deselectComplete,
  deselectGroups,
  onClose,
  parcelValues,
  updateRows,
  updatesPending,
  balance,
  balanceLoading,
  availableUsers,
  reloadBalance,
  resetSelections,
  onGroupsFulfillment,
  triggerRefresh,
  onVoidGroups,
  onChangeLocations,
  setRows,
  setSelectedFulfillmentGroups,
  setSearchRefreshSymbol,
  somePartiallySelected,
  selectFullGroups,
  doPartialSplit,
  partialSplitLoad,
  hideSlideout,
  onExportPickListCsv,
  onPrintPackingSlip,
  onPrintPickList,
  onPrintOrderSummary,
}: {
  fulfillmentGroups: readonly FulfillmentGroupData[];
  deselectComplete: () => void;
  deselectGroups?: (groupIds: string[]) => void;
  onClose: () => void;
  parcelValues: readonly Parcel[];
  updateRows: (updates: FulfillmentGroupUpdate[]) => void;
  updatesPending: boolean;
  balance: string | undefined;
  balanceLoading: boolean;
  availableUsers: OMSUser[];
  reloadBalance: () => void;
  resetSelections: () => void;
  triggerRefresh: () => void;
  onGroupsFulfillment: (groupIds: string[]) => void;
  onVoidGroups: (groups: string[]) => void;
  onChangeLocations: (groups: string[]) => void;
  setRows?: (value: React.SetStateAction<FulfillmentGroupData[]>) => void;
  setSelectedFulfillmentGroups?: (groups: FulfillmentGroupData[]) => void;
  setSearchRefreshSymbol: (value: symbol) => void;
  somePartiallySelected: boolean;
  selectFullGroups: () => void;
  doPartialSplit: () => void;
  partialSplitLoad: LoadState<void> | undefined;
  hideSlideout?: boolean;
  onExportPickListCsv: () => void;
  onPrintPackingSlip: () => void;
  onPrintPickList: () => void;
  onPrintOrderSummary: () => void;
}) {
  const mixedOpenness = useMemo(() => {
    const allStatuses = fulfillmentGroups.map((group) => group.status);
    return !allStatuses.every(
      (status) =>
        canPurchaseLabelStatus(allStatuses[0]) ===
        canPurchaseLabelStatus(status),
    );
  }, [fulfillmentGroups]);

  const canPurchaseEveryGroup = useMemo(() => {
    return fulfillmentGroups.every((group) =>
      canPurchaseLabelStatus(group.status),
    );
  }, [fulfillmentGroups]);

  const { permissionsToEditEveryGroup } =
    useFulfillmentGroupPermissions(fulfillmentGroups);

  const blurSlideout = useMemo(() => {
    return mixedOpenness || somePartiallySelected;
  }, [somePartiallySelected, mixedOpenness]);

  const { isOpen: aiPanelOpen, panelWidth: aiPanelWidth } =
    useAiChatPanelState();
  const aiPanelOffset = aiPanelOpen ? aiPanelWidth + 6 : 0;

  const paperStyle: CSSProperties = {
    overflowY: "visible",
    top: "var(--redo-top-nav-height)",
    height: "calc(100vh - var(--redo-top-nav-height))",
    right: aiPanelOffset,
  };

  return (
    <Drawer
      anchor="right"
      onClose={onClose}
      open={!!fulfillmentGroups.length && !hideSlideout}
      PaperProps={{ style: paperStyle }}
      variant="persistent"
    >
      <Flex
        className={ordersCss.drawer}
        data-testid="fulfillment-group-slideout"
        inert={blurSlideout}
        overflow={blurSlideout ? "hidden" : undefined}
        style={{ filter: blurSlideout ? "blur(4px)" : "none" }}
      >
        <FulfillmentForm
          availableUsers={availableUsers}
          balance={balance}
          balanceLoading={balanceLoading}
          deselectGroups={deselectGroups}
          fulfillmentGroups={fulfillmentGroups}
          onChangeLocations={onChangeLocations}
          onClose={onClose}
          onGroupsFulfillment={onGroupsFulfillment}
          onGroupsVoided={onVoidGroups}
          parcelValues={parcelValues}
          permissionsToEditEveryGroup={permissionsToEditEveryGroup}
          reloadBalance={reloadBalance}
          resetSelections={resetSelections}
          setRows={setRows}
          setSearchRefreshSymbol={setSearchRefreshSymbol}
          setSelectedFulfillmentGroups={setSelectedFulfillmentGroups}
          triggerRefresh={triggerRefresh}
          updateRows={updateRows}
          updatesPending={updatesPending}
          useDefaultActionButton
        />
      </Flex>
      {mixedOpenness ? (
        <FulfillmentMixedStatuses
          deselectComplete={deselectComplete}
          onExportPickListCsv={onExportPickListCsv}
          onPrintOrderSummary={onPrintOrderSummary}
          onPrintPackingSlip={onPrintPackingSlip}
          onPrintPickList={onPrintPickList}
        />
      ) : somePartiallySelected ? (
        canPurchaseEveryGroup ? (
          <FulfillmentPartiallySelectedSplit
            doPartialSplit={doPartialSplit}
            partialSplitLoad={partialSplitLoad}
            selectFullGroups={selectFullGroups}
          />
        ) : (
          <FulfillmentPartiallySelectedDeselect
            selectFullGroups={selectFullGroups}
          />
        )
      ) : null}
    </Drawer>
  );
});
